import { defineEffect } from '../../../core/effect';
import { seeded, seg, tween } from '../../../core/effect/motion';
import {
  add, canvasOf, fade, fill, place,
} from '../fx/common';
import { PLATE, centerColumn } from '../fx/chainPlate';
import {
  appearAlpha, appearAt, centerOf, linksUpTo, makeJoin, makePlate, placeJoin,
} from '../fx/chainColumn';

/**
 * **チェーンに積まれる**（Issue #576。指示書 `../direction/chain.md`）。チェーンスタックに 1 枚積まれたことを、
 * マスターデュエルのチェーン表示のように**画面の中央の列**で見せる。**盤には何も残さない**（常時表示はやめた。ユーザー 2026-09-28）。
 *
 * ```
 * 0     すでにあるチェーンの札（前のリンク。payload の chainLinks）が中央の列に短く出る
 *       （チェーン 1 が一番上、上から下へ。間を開けて鎖のつなぎでつながる）。盤・背景を少し暗くして札を浮かせる（札は暗くしない）
 * 200   発動したカードの札（イラスト・発動したプレイヤーの色の縁・チェーンの番号）がその人の側から少し大きく飛んできて、
 * 320   列の一番下に「バンッ！」と叩きつけられ、上の札と鎖でつながる → 止め 80ms（2 フレームの閃光・硬い火花）→ 小さな揺れ。上の札も揺れる
 * 400〜 見得
 * 760   列ごと縦に潰れてはける（暗さも戻る）
 * 880   何も描かない（帯の終わりまで）
 * ```
 *
 * 無効にされたリンクの札は、発動の無効（negated）ならヒビ、効果の無効（disabled）なら鎖で縛って描く（`../fx/chainPlate.js::paintPlate`。Issue #568）。
 * 札の大きさは一定。列が入り切らないときは新しい札から入るだけ見せ、古い札は列の上へはみ出して見えなくなる
 * （上端をまたぐ札は薄く途切れる。`../fx/chainPlate.js::centerColumn`）。重ねない。
 * 演出は payload とその瞬間の盤面だけ（積まれているリンクは payload の `chainLinks`）。もやもやした光は使わない。
 */
export const D = 900;
export const FLY = 200;      // 新しい札が飛び始める
export const HIT = 320;      // 叩きつけ
const STOP = 80;             // ヒットストップ
export const EXIT = 760;     // 列ごと潰れ始める
export const GONE = 880;     // 消え終わる
/** 盤・背景を暗くする濃さ（少し。札が浮いて見える程度。ユーザー 2026-09-28） */
export const DIM = 0.3;

const SPARKS = 18;
/** 飛んでくる札のはじめの大きさ（列の札に対する倍率）。1.8 だと画面の端で番号が巨大に見えたので抑えた（ユーザー 2026-09-28） */
const INCOMING_SCALE = 1.25;

/** このリンクまでの一覧（`{ chain, code, player, negated, disabled }`。チェーンの番号の順） */
export const linksOf = linksUpTo;

/** 時間の写し（止めの間は HIT で止まる） */
const hs = (t) => (t < HIT ? t : (t < HIT + STOP ? HIT : t - STOP));

/** 列が潰れる量（1 → 0） */
export const squashAt = (t) => 1 - tween(t, EXIT, GONE, 'power3_in');

export default defineEffect({
  situation: 'chained',
  role: 'effect',
  label: 'チェーンに積まれる',
  color: '#ffb020',
  // `chain` / `links`（このリンクまで）/ `player` / `code` / `uid` は分岐レイヤー（`editorPorts/duelSituations/chained.js`）が payload に付ける（Issue #589）
  duration: () => D,
  sounds: () => [{ at: HIT - 10, name: 'set_card', gain: 0.95 }],
  scene: ['reset',
    {
      ms: HIT,
      grade: {
        contrast: { keys: [{ ms: 0, value: 1.3 }, { ms: STOP, value: 1.3, ease: 'hold' }, { ms: STOP + 1, value: 1 }] },
        chroma: { keys: [{ ms: 0, value: 0.01 }, { ms: STOP + 90, value: 0, ease: 'ease_out' }] },
      },
    },
    { ms: HIT + STOP, shake: { amp: 14, freq: 28, decayMs: 200 } }],

  layers: [
    {
      id: 'column',
      label: 'チェーンの列（前の札 → 新しい札を下に叩きつけ → 列ごと潰れてはける）',
      space: 'screen',
      script: (ctx) => {
        const { THREE, kit } = ctx;
        const canvas = canvasOf(ctx);
        const { w: W, h: H } = canvas;
        const { payload } = ctx;
        const root = new THREE.Group();
        const links = payload?.links ?? [];
        const n = links.length;
        if (!n) return { root };
        const rand = seeded(`${ctx.seed}:chained`);
        const side = links[n - 1].player === 1 ? 1 : -1;
        const col = centerColumn(n, canvas, links);
        // 列の中心（潰れる軸）
        const midY = (col[0].y + col[n - 1].y + col[n - 1].h) / 2;

        // 盤・背景を少し暗く（札より奥）
        const dim = add(root, place(fill(ctx, '#040406', W, H), W / 2, H / 2), 0);

        /** 鎖のつなぎ（i 枚目と i+1 枚目の間。札より奥） */
        const joins = links.slice(1).map((_, i) => makeJoin(ctx, root, 9 + i * 0.01));

        /** 札（古い順に作る）。無効にされたリンクは発動の無効ならヒビ・効果の無効なら鎖で縛って描く（`paintPlate`） */
        const plates = links.map((l, i) => Object.assign(makePlate(ctx, l, 10 + i, root), { isNew: i === n - 1, index: i }));

        // 飛んでくる札の硬い線（3 本）と、叩きつけの火花（硬い三角）
        const top = new THREE.Group();
        add(root, top, 40);
        const trails = [-0.3, 0.02, 0.3].map((k, i) => ({ m: add(top, fill(ctx, i === 1 ? '#ffd21a' : '#ffffff', 1, 1), 0), k }));
        const sparkCols = ['#ffd21a', '#fff3b0', '#ffffff'];
        const sparks = Array.from({ length: SPARKS }, (_, i) => {
          const m = new THREE.Mesh(new THREE.CircleGeometry(1, 3), kit.plain(null, 1));
          m.material.color?.set?.(sparkCols[i % sparkCols.length]);
          add(top, m, 1);
          const a = rand() * Math.PI * 2;
          return {
            m, a, len: 40 + rand() * 70, w: 6 + rand() * 8, speed: 700 + rand() * 900, life: 150 + rand() * 90,
          };
        });

        return {
          root,
          update: ({ localMs: t }) => {
            const u = hs(t);
            const shown = t >= 0 && t < GONE;
            const q = squashAt(t);
            fade(dim, DIM * seg(t, 0, 150) * (1 - seg(t, EXIT, GONE)));

            const hitK = t >= HIT ? Math.sin(seg(u, HIT, HIT + 120) * Math.PI) * (1 - seg(u, HIT, HIT + 120)) : 0;
            const mien = 1 + 0.02 * seg(t, HIT + STOP, EXIT);
            plates.forEach((pl) => {
              // 画像が後から読めたら描き直す
              pl.refresh();
              fade(pl.grey, 0);
              const slot = centerOf(col[pl.index]);
              let at;
              let alpha;
              if (pl.isNew) {
                // 発動した人の側の外から、少し大きく傾いて加速して飛んできて、列の一番下に叩きつけられる
                const k = tween(t, FLY, HIT, 'power2_in');
                const from = { x: W / 2 + side * (W * 0.62), y: slot.y - 200, s: slot.s * INCOMING_SCALE, rot: side * 0.18 };
                at = {
                  x: from.x + (slot.x - from.x) * k,
                  y: from.y + (slot.y - from.y) * k,
                  s: (from.s + (slot.s - from.s) * k) * (1 - 0.05 * hitK),
                  rot: from.rot + (slot.rot - from.rot) * k,
                };
                alpha = t >= FLY ? 1 : 0;
              } else {
                // 前の札は列に短く出る（上から順に少しずつ遅れて）。叩きつけで揺れる
                const k = appearAt(t, pl.index);
                at = { x: slot.x, y: slot.y - 6 * hitK, s: slot.s * (0.9 + 0.1 * k), rot: slot.rot };
                alpha = appearAlpha(t, pl.index);
              }
              // 列の範囲の上へはみ出した古い札は見せない（途中までは薄く）
              alpha *= col[pl.index].fade;
              const s = at.s * mien;
              // 潰れるときは傾きも戻す
              pl.at = { x: at.x, y: midY + (at.y - midY) * q, s, sy: s * q, rot: at.rot * q };
              place(pl.g, pl.at.x, pl.at.y, { sx: s, sy: Math.max(0.001, s * q), rot: pl.at.rot });
              fade(pl.g, shown ? alpha : 0);
              fade(pl.flash, pl.isNew && t >= HIT && t < HIT + 34 ? 0.55 : 0);
            });

            // 鎖のつなぎ: 上の札の仕切りの下から下の札の仕切りの上へ（ずれていれば斜め）。新しい札へのつなぎは叩きつけた瞬間に掛かる
            joins.forEach((m, i) => {
              const dy = placeJoin(m, plates[i].at, plates[i + 1].at);
              const on = shown && dy > 1 && (!plates[i + 1].isNew || t >= HIT);
              fade(m, on ? appearAlpha(t, i + 1) * col[i].fade : 0);
            });

            // 飛んでくる札の硬い線（来た側へ伸び、叩きつけで縮む）
            const last = centerOf(col[n - 1]);
            const kIn = tween(t, FLY, HIT, 'power2_in');
            const nx = W / 2 + side * (W * 0.62) * (1 - kIn);
            const len = t < HIT ? W * 0.4 * seg(t, FLY, FLY + 40) : W * 0.4 * (1 - seg(u, HIT, HIT + 70));
            trails.forEach(({ m, k }) => {
              const tail = nx + side * PLATE.w * last.s * 0.5;
              place(m, tail + side * len / 2, last.y - 200 * (1 - kIn) + k * PLATE.h * last.s, { sx: Math.max(0.001, len), sy: k === 0.02 ? 6 : 3 });
              fade(m, t >= FLY && len > 1 ? 0.9 : 0);
            });

            // 火花（叩きつけた札の縁から外へ）
            const since = t - HIT;
            sparks.forEach((sp) => {
              const k = since / sp.life;
              if (since < 0 || k >= 1) { fade(sp.m, 0); return; }
              const r0 = PLATE.w * last.s * 0.42;
              const r = r0 + sp.speed * (since / 1000) * (1 - 0.4 * k);
              const l = sp.len * (1 - k);
              place(sp.m, last.x + Math.cos(sp.a) * (r + l / 2), last.y + Math.sin(sp.a) * (r * 0.45 + l / 2), {
                sx: Math.max(0.001, l), sy: sp.w * (1 - 0.5 * k), rot: sp.a,
              });
              fade(sp.m, 1);
            });
          },
        };
      },
    },
  ],
}, 'chained_effect.js');
